import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { CardQuotaRequestListRow } from '../../../../core/models/card-quota/card-quota-request.model';
import { MapperCardQuotaRequestService } from '../../../../core/mappers/card-quota/mapper-card-quota-request.service';
import { ApiCardQuotaService } from '../../../../core/services/api/card-quota/api-card-quota.service';
import { ClientAccessScopeService } from '../../../../core/services/client-access-scope.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { ModalQuotaRequestComponent } from '../../modals/card-quota/modal-quota-request/modal-quota-request.component';

type SecondaryFilterMode = '' | 'status' | 'request_type';

@Component({
  selector: 'app-quota-requests',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalQuotaRequestComponent],
  templateUrl: './quota-requests.component.html',
  styleUrl: './quota-requests.component.css',
})
export class QuotaRequestsComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  readonly clientScope = inject(ClientAccessScopeService);
  private readonly api = inject(ApiCardQuotaService);
  private readonly cookieService = inject(CookieService);
  private readonly mapper = inject(MapperCardQuotaRequestService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  items = signal<CardQuotaRequestListRow[]>([]);
  isLoading = signal(false);
  isModalOpen = signal(false);
  selectedRequestId = signal(0);

  currentPage = signal(1);
  pageSize = signal(20);
  pageSizeOptions = [20, 50, 100];
  totalItems = signal(0);
  totalPages = signal(0);

  dateFrom = signal(this.defaultDateFrom());
  dateTo = signal(this.defaultDateTo());
  secondaryFilterMode = signal<SecondaryFilterMode>('');
  filterStatus = signal('');
  filterRequestType = signal('');

  searchField = signal<'name' | 'client'>('name');
  searchText = signal('');

  readonly searchFieldOptions = [
    { value: 'name' as const, label: 'เลขที่คำขอ' },
    { value: 'client' as const, label: 'Client / ร้าน' },
  ];

  readonly requestTypeOptions = [
    { value: '', label: 'ทุกประเภท' },
    { value: 'increase', label: 'ขอเพิ่ม' },
    { value: 'decrease', label: 'ขอลด' },
    { value: 'suspend', label: 'ระงับ' },
    { value: 'resume', label: 'ยกเลิกระงับ' },
  ];

  readonly statusOptions = [
    { value: '', label: 'ทุกสถานะ' },
    { value: 'draft', label: 'Draft' },
    { value: 'pending', label: 'รออนุมัติ' },
    { value: 'approved', label: 'อนุมัติแล้ว' },
    { value: 'rejected', label: 'ปฏิเสธ' },
    { value: 'cancelled', label: 'ยกเลิก' },
  ];

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) return;
      untracked(() => {
        this.currentPage.set(1);
        this.loadScopeAndTable();
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  ngOnInit(): void {
    if (this.selectedSiteId) this.loadScopeAndTable();
  }

  requestTypeLabel = (type: string) => this.mapper.requestTypeLabel(type);
  statusLabel = (status: string) => this.mapper.statusLabel(status);

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadScopeAndTable(true);
  }

  onDateChange(): void {
    if (!this.validateDateRange()) return;
    this.currentPage.set(1);
    this.loadTable();
  }

  onSearch(): void {
    if (!this.validateDateRange()) return;
    this.currentPage.set(1);
    this.loadTable();
  }

  onSecondaryFilterModeChange(mode: SecondaryFilterMode): void {
    this.secondaryFilterMode.set(mode);
    if (mode !== 'status') this.filterStatus.set('');
    if (mode !== 'request_type') this.filterRequestType.set('');
    this.onSearch();
  }

  onStatusChange(value: string): void {
    this.secondaryFilterMode.set(value ? 'status' : '');
    this.filterStatus.set(value);
    this.filterRequestType.set('');
    this.onSearch();
  }

  onRequestTypeChange(value: string): void {
    this.secondaryFilterMode.set(value ? 'request_type' : '');
    this.filterRequestType.set(value);
    this.filterStatus.set('');
    this.onSearch();
  }

  applyPresetDays(days: number): void {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    this.dateTo.set(this.toDateInput(to));
    this.dateFrom.set(this.toDateInput(from));
    this.onDateChange();
  }

  applyThisMonth(): void {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    this.dateFrom.set(this.toDateInput(from));
    this.dateTo.set(this.toDateInput(now));
    this.onDateChange();
  }

  clearSearchText(): void {
    this.searchText.set('');
    this.onSearch();
  }

  loadScopeAndTable(forceScope = false): void {
    const siteId = this.selectedSiteId;
    const userId = Number(this.cookieService.get('user_id') || 0);
    if (!siteId) {
      this.items.set([]);
      return;
    }

    this.isLoading.set(true);
    this.clientScope.load(siteId, userId, forceScope).subscribe({
      next: () => this.loadTable(),
      error: () => this.loadTable(),
    });
  }

  openCreate(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    this.selectedRequestId.set(0);
    this.isModalOpen.set(true);
  }

  openEdit(item: CardQuotaRequestListRow): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!['draft', 'rejected'].includes(item.status)) {
      this.toast.error('แก้ไขได้เฉพาะสถานะ draft / rejected');
      return;
    }
    this.selectedRequestId.set(item.id);
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedRequestId.set(0);
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadTable();
    }
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize.set(Number(element.value));
    this.currentPage.set(1);
    this.loadTable();
  }

  trackById(_index: number, item: CardQuotaRequestListRow): number {
    return item.id;
  }

  get pagesArray(): number[] {
    const total = this.totalPages();
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage() - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.items.set([]);
      return;
    }

    if (!this.validateDateRange()) {
      this.isLoading.set(false);
      return;
    }

    const trimmedSearch = this.searchText().trim();
    const searchField = trimmedSearch ? this.searchField() : null;
    const searchText = trimmedSearch || null;

    this.isLoading.set(true);
    this.api
      .getCardQuotaRequestsForTable(
        siteId,
        this.currentPage(),
        this.pageSize(),
        null,
        this.secondaryFilterMode() === 'status' ? this.filterStatus() || null : null,
        this.secondaryFilterMode() === 'request_type' ? this.filterRequestType() || null : null,
        this.dateFrom(),
        this.dateTo(),
        searchField,
        searchText
      )
      .subscribe({
        next: (res) => {
          const mapped = this.mapper.mapTable(res);
          this.items.set(mapped.items);
          this.totalItems.set(mapped.total);
          this.totalPages.set(mapped.totalPages || Math.max(1, Math.ceil(mapped.total / this.pageSize())));
          this.isLoading.set(false);
        },
        error: () => {
          this.items.set([]);
          this.isLoading.set(false);
          this.toast.error('โหลดรายการคำขอล้มเหลว');
        },
      });
  }

  private validateDateRange(): boolean {
    if (!this.dateFrom() || !this.dateTo()) {
      this.toast.error('กรุณาเลือกช่วงวันที่');
      return false;
    }
    if (this.dateFrom() > this.dateTo()) {
      this.toast.error('วันที่เริ่มต้องไม่เกินวันที่สิ้นสุด');
      return false;
    }
    return true;
  }

  private defaultDateFrom(): string {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return this.toDateInput(d);
  }

  private defaultDateTo(): string {
    return this.toDateInput(new Date());
  }

  private toDateInput(d: Date): string {
    return d.toISOString().slice(0, 10);
  }
}
